import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CalendarOff, CheckCircle2, Clock } from 'lucide-react';
import { cpdAPI, errMsg } from '../../lib/cpdRegisterAPI';
import { formatAUDate } from '../../lib/dateUtils';
import { toast } from 'sonner';

export const CPDExceptions = ({ userId, me, items, note, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: '', period_start: '', period_end: '', explanation: '' });
  const canAdd = me.tier !== 'auditor' && (userId === me.user_id || me.tier === 'admin');
  const save = async () => {
    try {
      await cpdAPI.addException({ ...f, user_id: userId });
      toast.success('Exception recorded');
      setOpen(false);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async (id) => { try { await cpdAPI.approveException(id); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card data-testid="cpd-exceptions-card">
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base flex items-center gap-2"><CalendarOff className="w-4 h-4" /> Leave & exceptions</CardTitle>
        {canAdd && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="cpd-add-exception-btn">Record exception</Button>}
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">{note} Exceptions never automatically satisfy CPD requirements.</p>
        {items.length === 0 && <p className="text-sm text-muted-foreground">None recorded.</p>}
        {items.map((x) => (
          <div key={x.id} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm" data-testid={`cpd-exception-${x.id}`}>
            <div><b>{x.type}</b> · {formatAUDate(x.period_start)} – {formatAUDate(x.period_end)}<p className="text-xs text-muted-foreground">{x.explanation}</p></div>
            {x.status === 'approved' ? <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Approved</span>
              : me.tier === 'admin' ? <Button size="sm" variant="outline" onClick={() => approve(x.id)} data-testid={`cpd-approve-exception-${x.id}`}>Approve</Button>
                : <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Pending</span>}
          </div>
        ))}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cpd-exception-dialog">
          <DialogHeader><DialogTitle>Record leave / exception</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger data-testid="cpd-exception-type"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{me.exception_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From</Label><Input type="date" value={f.period_start} onChange={(e) => setF({ ...f, period_start: e.target.value })} data-testid="cpd-exception-from" /></div>
              <div><Label>To</Label><Input type="date" value={f.period_end} onChange={(e) => setF({ ...f, period_end: e.target.value })} data-testid="cpd-exception-to" /></div>
            </div>
            <div><Label>Explanation</Label><Textarea value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} data-testid="cpd-exception-explanation" /></div>
            <p className="text-xs text-muted-foreground">Recorded Exception – Regulatory eligibility should be independently assessed.</p>
          </div>
          <DialogFooter><Button onClick={save} data-testid="cpd-exception-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
